import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import TempleQuote from "../components/TempleQuote";

// Locked YouTube identifiers
const YT_CHANNEL = "https://www.youtube.com/@MindBodySpiritHygiene";
const YT_CHANNEL_ID = "UCSZEC8QytHqYM8svPb-9L4Q";
// Uploads playlist = channel ID with second char swapped from C to U
const YT_UPLOADS_PLAYLIST = "UU" + YT_CHANNEL_ID.slice(2);
const YT_EMBED_URL = `https://www.youtube.com/embed/videoseries?list=${YT_UPLOADS_PLAYLIST}&rel=0&modestbranding=1`;

// SEO-rich page metadata
const PAGE_URL = "https://mindbodyspirithygienetools.com/card-pulls";
const PAGE_TITLE = "Daily Card Pulls · Free Tarot, Oracle & Spirit Readings — Temple Franklin";
const PAGE_DESC = "Free daily card pulls by Temple Franklin — tarot, oracle, animal spirit & archangel readings for the woman tapping into the energies of her day. New pull every morning on YouTube. Grounded, Christian-friendly, no toxic positivity.";
const PAGE_IMAGE = "https://mindbodyspirithygienetools.com/cards/00-card-back.png";

// FAQ content — visible on page AND in JSON-LD schema for rich SERP results
const FAQ = [
  {
    q: "Are daily card pulls Christian?",
    a: "These pulls are for women who hear God in many languages — angels, ancestors, animals, scripture, kitchen-window light at 7am. I am a vessel, not a fortune teller. I never replace your faith — I sit beside it. Take what resonates. Leave the rest.",
  },
  {
    q: "How often do you upload card pulls?",
    a: "A fresh pull every morning on YouTube @MindBodySpiritHygiene. Subscribe and hit the bell so you do not miss yours.",
  },
  {
    q: "What decks do you use for your readings?",
    a: "Tarot, oracle, animal spirit, archangel — and my own 58-card Mind Body Spirit HYGIENE™ deck. Which deck I pull from depends on the energy of the day and what Divine moves me to bring forward.",
  },
  {
    q: "Are these card pull readings free?",
    a: "Yes. Free. Forever. The daily pulls live on YouTube at no cost. The 58-card deck and companion guide are available for purchase if you want the cards in your hands.",
  },
  {
    q: "Can I do my own card pull right now?",
    a: "Yes — visit our interactive 58-card draw at /draw to pull a card from my own deck right now. One card, one message, the practice of the day.",
  },
  {
    q: "Are card pulls a substitute for therapy or medical advice?",
    a: "No. These pulls are for inspiration, reflection, and a daily practice of tuning in. They are NOT a substitute for medical, psychological, financial, legal, or therapeutic advice. Please consult qualified professionals for those needs.",
  },
];

// JSON-LD builders
const buildSchemaScripts = () => {
  const webpage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": PAGE_URL,
    name: PAGE_TITLE,
    url: PAGE_URL,
    description: PAGE_DESC,
    inLanguage: "en-US",
    isPartOf: { "@type": "WebSite", "@id": "https://mindbodyspirithygienetools.com/#website" },
    breadcrumb: { "@id": `${PAGE_URL}#breadcrumb` },
    author: { "@type": "Person", name: "Temple Franklin", "@id": "https://mindbodyspirithygienetools.com/#temple" },
    mainEntity: { "@id": `${PAGE_URL}#faq` },
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${PAGE_URL}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://mindbodyspirithygienetools.com/" },
      { "@type": "ListItem", position: 2, name: "Daily Card Pulls", item: PAGE_URL },
    ],
  };

  const video = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "Daily Card Pulls with Temple Franklin",
    description: "Daily tarot, oracle, animal spirit and archangel card pull readings for women — grounded daily practice from Temple Franklin, Mind Body Spirit HYGIENE™ Tools.",
    thumbnailUrl: [PAGE_IMAGE],
    uploadDate: new Date().toISOString().split("T")[0],
    contentUrl: YT_CHANNEL,
    embedUrl: `https://www.youtube.com/embed/videoseries?list=${YT_UPLOADS_PLAYLIST}`,
    publisher: {
      "@type": "Organization",
      name: "Mind Body Spirit HYGIENE™ Tools",
      logo: { "@type": "ImageObject", url: "https://mindbodyspirithygienetools.com/logo512.png" },
    },
  };

  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${PAGE_URL}#faq`,
    mainEntity: FAQ.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return [webpage, breadcrumb, video, faq];
};

const CardPulls = () => {
  useEffect(() => {
    // Title
    document.title = PAGE_TITLE;

    // Helper: upsert <meta name|property="..." content="...">
    const upsert = (key, attr, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    upsert("description", "name", PAGE_DESC);
    upsert(
      "keywords",
      "name",
      "daily card pulls, free tarot reading, oracle card reading, daily card reading, christian tarot, archangel cards, animal spirit cards, Temple Franklin, Mind Body Spirit HYGIENE, daily spiritual practice, card pull YouTube"
    );

    // Open Graph
    upsert("og:type", "property", "video.other");
    upsert("og:title", "property", PAGE_TITLE);
    upsert("og:description", "property", PAGE_DESC);
    upsert("og:url", "property", PAGE_URL);
    upsert("og:image", "property", PAGE_IMAGE);
    upsert("og:site_name", "property", "Mind Body Spirit HYGIENE™ Tools");

    // Twitter
    upsert("twitter:card", "name", "summary_large_image");
    upsert("twitter:title", "name", PAGE_TITLE);
    upsert("twitter:description", "name", PAGE_DESC);
    upsert("twitter:image", "name", PAGE_IMAGE);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", PAGE_URL);

    // JSON-LD structured data
    const scripts = buildSchemaScripts().map((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-card-pulls-seo", "1");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      // cleanup our JSON-LD on unmount so other pages don't inherit it
      document
        .querySelectorAll('script[data-card-pulls-seo="1"]')
        .forEach((s) => s.remove());
    };
  }, []);

  const todayPretty = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-[#efeae7]" data-testid="card-pulls-page">
      {/* HERO */}
      <section className="pt-24 pb-12 px-6 md:pt-32 md:pb-16" data-testid="card-pulls-hero">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#25a1c2] font-medium mb-5" data-testid="card-pulls-eyebrow">
            A daily practice · Live on YouTube
          </p>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tighter text-[#042F2E] leading-[0.95] mb-6" data-testid="card-pulls-headline">
            Daily Card Pulls<br />
            <span className="italic text-[#25a1c2]">with Temple</span>
          </h1>
          <p className="font-serif italic text-2xl md:text-3xl text-[#068fa2] mb-8 max-w-2xl mx-auto" data-testid="card-pulls-tagline">
            For tapping into the energies of your day.
          </p>
          <div className="text-[#068fa2] text-[17px] leading-relaxed max-w-2xl mx-auto mb-10 space-y-4" data-testid="card-pulls-intro">
            <p>
              I will be using different decks depending on the energy and what Divine pulls me to share.
            </p>
            <p>
              Join me each day for some inspiration, love, messages, new perspectives — and some giggles.
            </p>
            <p className="font-serif italic text-[#042F2E]">
              Remember: you have free will and your own discernment. Take what resonates and leave the rest.
            </p>
            <p className="font-serif italic text-[#042F2E]">
              Blessings,<br />Temple
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3" data-testid="card-pulls-cta-row">
            <a
              href={YT_CHANNEL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25a1c2] hover:bg-[#068fa2] text-white font-sans font-semibold px-7 py-3 rounded-full text-sm tracking-wide transition-colors"
              data-testid="cta-subscribe-youtube"
            >
              Subscribe on YouTube →
            </a>
            <Link
              to="/workbook?from=card-pulls"
              className="bg-[#042F2E] hover:bg-[#068fa2] text-white font-sans font-semibold px-7 py-3 rounded-full text-sm tracking-wide transition-colors"
              data-testid="cta-free-journal"
            >
              Get the free Validation Journal
            </Link>
          </div>
        </div>
      </section>

      {/* LIVE EMBED — LATEST UPLOAD */}
      <section className="pb-16 px-6" data-testid="card-pulls-latest-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#25a1c2] font-medium" data-testid="latest-eyebrow">
              Today's Pull · {todayPretty}
            </p>
          </div>
          <div className="relative w-full aspect-video rounded-3xl overflow-hidden shadow-2xl bg-[#042F2E]" data-testid="latest-embed">
            <iframe
              className="absolute inset-0 w-full h-full"
              src={YT_EMBED_URL}
              title="Daily card pulls — free tarot, oracle and spirit readings by Temple Franklin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
          <p className="text-center font-sans text-sm text-[#475569] mt-4" data-testid="embed-note">
            Can't see the video? <a href={YT_CHANNEL} target="_blank" rel="noopener noreferrer" className="text-[#25a1c2] underline">Watch on YouTube directly →</a>
          </p>

          {/* Internal cross-link — pull your own card right now */}
          <div className="mt-8 text-center" data-testid="self-pull-link">
            <Link
              to="/draw"
              className="inline-flex items-center gap-2 font-serif italic text-lg text-[#068fa2] hover:text-[#042F2E] underline decoration-[#25a1c2] decoration-2 underline-offset-4"
              data-testid="pull-your-own-link"
            >
              Want to pull your own card right now? Draw one from my 58-card deck →
            </Link>
          </div>
        </div>
      </section>

      {/* TEMPLE QUOTE — Why these pulls exist */}
      <TempleQuote
        quote="Tapped In. Grounded. Then back to the kitchen table."
        variant="hero"
        theme="cream"
        id="quote-tapped-in"
      />

      {/* HOW THE PULLS WORK */}
      <section className="py-16 px-6 bg-white" data-testid="how-it-works">
        <div className="max-w-4xl mx-auto">
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#25a1c2] font-medium mb-5 text-center" data-testid="how-eyebrow">How the pulls work</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#042F2E] text-center mb-12 leading-tight" data-testid="how-headline">
            Random pulls. Real messages.<br />
            <span className="italic text-[#25a1c2]">Daily Tap In practice.</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-testid="how-grid">
            {[
              { num: "1", title: "I tap in", body: "Each morning I sit with the decks, ground in, and ask for the message Divine pulls me to share that day. No script. No editing. What comes through is what comes through." },
              { num: "2", title: "I pull what's drawn", body: "Tarot. Oracle. Sometimes animal spirit. Sometimes archangel. The decks change depending on the energy of the day and what Divine pulls me to bring forward." },
              { num: "3", title: "You walk it out", body: "Take what resonates. Leave the rest. Use your own free will and discernment. The message is an offering — what you do with it is yours." },
            ].map((step) => (
              <div key={step.num} className="bg-[#efeae7] rounded-2xl p-7 border border-[#5EEAD4]/40" data-testid={`step-${step.num}`}>
                <div className="font-serif italic text-5xl text-[#25a1c2] mb-3">{step.num}</div>
                <h3 className="font-serif text-2xl text-[#042F2E] mb-3 leading-tight">{step.title}</h3>
                <p className="font-sans text-[15px] text-[#475569] leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 max-w-2xl mx-auto text-center" data-testid="future-note">
            <p className="font-serif italic text-lg text-[#068fa2] leading-relaxed">
              <span className="text-[#25a1c2]">Coming once we find our rhythm:</span><br />
              Three-pile pick-a-pile pulls. Until then — these are collective daily pulls. One card. One message. Yours if it lands.
            </p>
          </div>
        </div>
      </section>

      {/* FREE WILL + DISCLAIMER — Temple-voiced integrity statement */}
      <section className="py-16 px-6 bg-white" data-testid="free-will-section">
        <div className="max-w-2xl mx-auto">
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#25a1c2] font-medium mb-5 text-center" data-testid="free-will-eyebrow">
            Free will · Discernment · Integrity
          </p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tighter text-[#042F2E] text-center leading-tight mb-8" data-testid="free-will-headline">
            A note from <span className="italic text-[#25a1c2]">Temple.</span>
          </h2>
          <div className="space-y-4 text-[#068fa2] leading-relaxed text-[16px] font-serif italic" data-testid="free-will-body">
            <p>
              The messages I bring through are offerings — not guarantees. There are no certainties when working with Divine energy. I am a vessel, not a fortune teller.
            </p>
            <p>
              You have your own free will. You have your own discernment. Take what resonates and leave the rest. The message lives in you, not in me.
            </p>
            <p className="text-[#042F2E]">
              These pulls are for inspiration, reflection, and a daily practice of tuning in. They are <span className="not-italic font-semibold">not</span> a substitute for medical, psychological, financial, legal, or therapeutic advice. Please consult qualified professionals for those needs. Your health — mental, physical, spiritual — is yours to steward.
            </p>
            <p>
              Always in high integrity. Always in Divine light.
            </p>
            <p className="text-[#042F2E] not-italic font-serif text-lg pt-2">
              Blessings,<br />— Temple
            </p>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-16 px-6" data-testid="who-its-for">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#25a1c2] font-medium mb-5" data-testid="who-eyebrow">Who these pulls are for</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#042F2E] leading-[0.95] mb-8" data-testid="who-headline">
            For the woman who is tired of <span className="italic text-[#25a1c2]">floaty wellness.</span>
          </h2>
          <div className="space-y-4 text-[#068fa2] leading-relaxed text-[17px]" data-testid="who-body">
            <p>The Christian woman who has been told tarot is forbidden — but knows in her bones that the Divine speaks in many languages.</p>
            <p>The ex-evangelical woman finding her own path home — ready to hear from God, angels, ancestors, and animals all in the same morning.</p>
            <p>The skeptic who would never click a "psychic reading" — but stays for the practical message at the end.</p>
            <p>The midlife woman in the messy middle who needs a grounded daily anchor — not another influencer telling her she's "manifesting" her overwhelm.</p>
            <p className="font-serif italic text-xl text-[#042F2E] pt-4">If that's you — these pulls are for you. Walk in.</p>
          </div>
        </div>
      </section>

      {/* CHANNEL CTA */}
      <section className="py-16 px-6" data-testid="channel-cta">
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#042F2E] via-[#068fa2] to-[#25a1c2] rounded-3xl p-10 md:p-14 text-white text-center">
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#5EEAD4] font-medium mb-5" data-testid="channel-cta-eyebrow">@MindBodySpiritHygiene</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tighter mb-5 leading-tight" data-testid="channel-cta-headline">
            New pull every morning.<br />
            <span className="italic">Subscribe so you don't miss yours.</span>
          </h2>
          <p className="font-serif italic text-lg text-white/80 mb-8 max-w-xl mx-auto" data-testid="channel-cta-body">
            Free. Forever. Hit the bell for daily notifications.
          </p>
          <a
            href={YT_CHANNEL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-[#042F2E] hover:bg-[#5EEAD4] font-sans font-semibold px-8 py-4 rounded-full text-sm tracking-wide transition-colors"
            data-testid="channel-cta-button"
          >
            Subscribe on YouTube →
          </a>
        </div>
      </section>

      {/* FAQ — SEO rich result eligible */}
      <section className="py-20 px-6 bg-white" data-testid="card-pulls-faq-section">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#25a1c2] font-medium mb-5 text-center" data-testid="faq-eyebrow">
            Honest answers
          </p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#042F2E] text-center leading-[0.95] mb-12" data-testid="faq-headline">
            About the daily <span className="italic text-[#25a1c2]">card pulls.</span>
          </h2>
          <div className="space-y-4" data-testid="faq-list">
            {FAQ.map(({ q, a }, i) => (
              <details
                key={i}
                className="group bg-[#efeae7] rounded-2xl p-6 border border-[#5EEAD4]/40 open:shadow-md transition-shadow"
                data-testid={`faq-item-${i}`}
              >
                <summary className="cursor-pointer font-serif text-xl text-[#042F2E] leading-snug flex justify-between items-start gap-4 list-none">
                  <span>{q}</span>
                  <span className="text-[#25a1c2] font-sans text-2xl leading-none transform transition-transform group-open:rotate-45 shrink-0">+</span>
                </summary>
                <p className="font-sans text-[15px] leading-relaxed text-[#475569] mt-4">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CardPulls;
